import { Pagination } from 'shared/types/types';
import { edpApiV1 } from '../index';

export interface TDayOffRequest {
  type: string;
  date: string;
  amount: number;
  comment: string;
  id: number;
  created: string;
  status: string;
  docGuid: string;
  docNumber: string;
  docDate: string;
  isComplete: boolean;
  docIsUpdated: boolean;
  organizationId: string;
  rejectionReason: string;
}

export interface TDayOffSupervisorResponse {
  employee: {
    id: number;
    name: string;
    lastName: string;
    middleName: string;
    position: {
      id: string;
      name: string;
      validFrom: string;
      validTo: string;
    };
    division: {
      id: string;
      code: string;
      name: string;
      validFrom: string;
      validTo: string;
    };
    image: {
      id: number;
      filename: string;
      path: string;
      uri: string;
    };
  };
  request: TDayOffRequest;
  canceledRequest?: {
    id: number;
    status: string;
    created: string;
  };
}

export type TPerparedDayOffSupervisor = {
  amount: number;
  created: string;
  id: number;
  date: string;
  status: string;
  fio: string;
  key: number;
  rejectionReason?: string;
};

export interface TDayOffSupervisorResponsePaginated {
  data: TDayOffSupervisorResponse[];
  pagination: Pagination;
}

export interface TDayOffsGetRequest {
  isArchive: boolean;
  page: number;
  size: number;
  divisionId?: string;
  byId?: string;
  fio?: string;
  personnelNumber?: string;
  positionName?: string;
}

export interface TSingleDayOffGetRequest {
  requestId: number;
  employeeId: number;
  divisionId?: string;
}

export interface TRejectDayOffGetRequest extends TSingleDayOffGetRequest {
  rejectionReason: string;
}

export const dayoffsSupervisorApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    dayoffsDeputy: build.query<TDayOffSupervisorResponsePaginated, TDayOffsGetRequest>({
      query: payload => {
        const { divisionId, ...params } = payload;
        return {
          url: `/v1/dublicate/deputy/vacation-application/subordinates/requests/time-off/${divisionId}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['dayoffDeputy'],
    }),
    singleDayoffDeputy: build.query<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId }) => ({
        url: `/v1/dublicate/deputy/vacation-application/subordinates/time-off/${divisionId}/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['dayoffDeputy'],
    }),
    approveDayoffDeputy: build.mutation<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId }) => ({
        url: `/v1/dublicate/deputy/vacation-application/subordinates/time-off/${divisionId}/${employeeId}/requests/${requestId}/approve`,
        method: 'PATCH',
      }),
      invalidatesTags: ['dayoffDeputy'],
    }),
    rejectDayoffDeputy: build.mutation<unknown, TRejectDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId, ...body }) => ({
        url: `/v1/dublicate/deputy/vacation-application/subordinates/time-off/${divisionId}/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['dayoffDeputy'],
    }),
    dayoffsSupervisor: build.query<TDayOffSupervisorResponsePaginated, TDayOffsGetRequest>({
      query: payload => ({
        url: `/v1/dublicate/supervisor/vacation-application/subordinates/requests/time-off`,
        method: 'GET',
        params: payload,
      }),
      providesTags: ['dayoffSupervisor'],
    }),
    singleDayoffSupervisor: build.query<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId }) => ({
        url: `/v1/dublicate/supervisor/vacation-application/subordinates/time-off/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['dayoffSupervisor'],
    }),
    approveDayoffSupervisor: build.mutation<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId }) => ({
        url: `/v1/dublicate/supervisor/vacation-application/subordinates/time-off/${employeeId}/requests/${requestId}/approve`,
        method: 'PATCH',
      }),
      invalidatesTags: ['dayoffSupervisor'],
    }),
    rejectDayoffSupervisor: build.mutation<unknown, TRejectDayOffGetRequest>({
      query: ({ employeeId, requestId, ...body }) => ({
        url: `/v1/dublicate/supervisor/vacation-application/subordinates/time-off/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['dayoffSupervisor'],
    }),
    dayoffsActing: build.query<TDayOffSupervisorResponsePaginated, TDayOffsGetRequest>({
      query: ({ divisionId, ...params }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-application/subordinates/requests/time-off`,
        method: 'GET',
        params,
      }),
      providesTags: ['dayoffActing'],
    }),
    singleDayoffActing: build.query<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-application/subordinates/time-off/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['dayoffActing'],
    }),
    approveDayoffActing: build.mutation<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-application/subordinates/time-off/${employeeId}/requests/${requestId}/approve`,
        method: 'PATCH',
      }),
      invalidatesTags: ['dayoffActing'],
    }),
    rejectDayoffActing: build.mutation<unknown, TRejectDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId, ...body }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-application/subordinates/time-off/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['dayoffActing'],
    }),
    dayoffsCanceledDeputy: build.query<TDayOffSupervisorResponsePaginated, TDayOffsGetRequest>({
      query: payload => {
        const { divisionId, ...params } = payload;
        return {
          url: `/v1/dublicate/deputy/vacation-application/subordinates/requests/time-off-canceled/${divisionId}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['dayoffsCanceledDeputy'],
    }),
    singleDayoffCanceledDeputy: build.query<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId }) => ({
        url: `/v1/dublicate/deputy/vacation-application/subordinates/time-off-canceled/${divisionId}/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['dayoffsCanceledDeputy'],
    }),
    approveDayoffCanceledDeputy: build.mutation<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId }) => ({
        url: `/v1/dublicate/deputy/vacation-application/subordinates/time-off-canceled/${divisionId}/${employeeId}/requests/${requestId}/approve`,
        method: 'PATCH',
      }),
      invalidatesTags: ['dayoffsCanceledDeputy'],
    }),
    rejectDayoffCanceledDeputy: build.mutation<unknown, TRejectDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId, ...body }) => ({
        url: `/v1/dublicate/deputy/vacation-application/subordinates/time-off-canceled/${divisionId}/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['dayoffsCanceledDeputy'],
    }),
    dayoffsCanceledSupervisor: build.query<TDayOffSupervisorResponsePaginated, TDayOffsGetRequest>({
      query: payload => ({
        url: `/v1/dublicate/supervisor/vacation-application/subordinates/requests/time-off-canceled`,
        method: 'GET',
        params: payload,
      }),
      providesTags: ['dayoffsCanceledSupervisor'],
    }),
    singleDayoffCanceledSupervisor: build.query<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId }) => ({
        url: `/v1/dublicate/supervisor/vacation-application/subordinates/time-off-canceled/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['dayoffsCanceledSupervisor'],
    }),
    approveDayoffCanceledSupervisor: build.mutation<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId }) => ({
        url: `/v1/dublicate/supervisor/vacation-application/subordinates/time-off-canceled/${employeeId}/requests/${requestId}/approve`,
        method: 'PATCH',
      }),
      invalidatesTags: ['dayoffsCanceledSupervisor'],
    }),
    rejectDayoffCanceledSupervisor: build.mutation<unknown, TRejectDayOffGetRequest>({
      query: ({ employeeId, requestId, ...body }) => ({
        url: `/v1/dublicate/supervisor/vacation-application/subordinates/time-off-canceled/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['dayoffsCanceledSupervisor'],
    }),
    dayoffsCanceledActing: build.query<TDayOffSupervisorResponsePaginated, TDayOffsGetRequest>({
      query: ({ divisionId, ...params }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-application/subordinates/requests/time-off-canceled`,
        method: 'GET',
        params,
      }),
      providesTags: ['dayoffsCanceledActing'],
    }),
    singleDayoffCanceledActing: build.query<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-application/subordinates/time-off-canceled/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['dayoffsCanceledActing'],
    }),
    approveDayoffCanceledActing: build.mutation<TDayOffSupervisorResponse, TSingleDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-application/subordinates/time-off-canceled/${employeeId}/requests/${requestId}/approve`,
        method: 'PATCH',
      }),
      invalidatesTags: ['dayoffsCanceledActing'],
    }),
    rejectDayoffCanceledActing: build.mutation<unknown, TRejectDayOffGetRequest>({
      query: ({ employeeId, requestId, divisionId, ...body }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-application/subordinates/time-off-canceled/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['dayoffsCanceledActing'],
    }),
  }),
  overrideExisting: false,
});
